import {
  MaskedInput,
  TextInput,
  Select,
  DatePicker,
  Checkbox,
} from "components/inputs";

import type { SubPageType } from "types/FormTypes";
import type { ConstantTypes } from "types/DataTypes";

export const getRReForms = (constants: ConstantTypes): SubPageType[] => {
  const states =
    constants?.STATES?.map((s) => ({
      value: s.value,
      label: s.label,
    })) || [];
  const linesOfBusiness =
    constants?.PLAN_INSURANCE_TYPE.map((t) => ({
      value: t.value,
      label: t.label,
    })) || [];
  const reporterTypes =
    constants?.RRE_REPORTER_TYPES.map((r) => ({
      value: r.value,
      label: r.label,
    })) || [];
  const operationUsers =
    constants?.OPERATIONS_USERS.map((r) => ({
      value: r.value,
      label: r.label,
    })) || [];
  return [
    {
      name: "Overview",
      stepNumber: 1,
      path: "overview",
      cards: [
        {
          cardNumber: 1,
          cardName: "RRE Information",
          fields: [
            {
              label: "CMS RRE ID",
              component: TextInput,
              apiField: "rre_id",
              componentProps: {
                placeholder: "RRE ID",
                readOnly: true,
              },
            },
            {
              label: "RRE Name",
              component: TextInput,
              apiField: "name",
              componentProps: {},
            },
            {
              label: "Address",
              component: TextInput,
              apiField: "street",
              componentProps: {},
            },
            {
              label: "City",
              component: TextInput,
              apiField: "city",
              componentProps: {},
            },
            {
              label: "State",
              component: Select,
              apiField: "state",
              componentProps: {
                options: [
                  // { value: "", label: "- Choose A State -" },
                  ...states,
                ],
              },
            },
            {
              label: "Zip",
              component: MaskedInput,
              apiField: "zipcode",
              componentProps: {
                mask: "zipcode",
              },
            },
            {
              label: "Reporting history existed prior to onboarding",
              component: Checkbox,
              apiField: "reporting_existed_prior_to_onboarding",
              componentProps: {
                readOnly: true,
              },
            },
          ],
        },
        {
          cardNumber: 2,
          cardName: "Company Information",
          fields: [
            {
              label: "Report Date",
              component: DatePicker,
              apiField: "profile_report_date",
              componentProps: {
                placeholder: "Report Date",
              },
            },
            {
              label: "Profile Change Date",
              component: DatePicker,
              apiField: "profile_changed_date",
              componentProps: {
                placeholder: "Profile Change Date",
              },
            },
            {
              label: "NAIC",
              component: TextInput,
              apiField: "naic",
              componentProps: {
                placeholder: "NAIC",
              },
            },
            {
              label: "Line of Business",
              component: Select,
              apiField: "line_of_business",
              componentProps: {
                options: [...linesOfBusiness],
              },
            },
            {
              label: "Reporter Type",
              component: Select,
              apiField: "reporter_type",
              componentProps: {
                options: [...reporterTypes],
              },
            },
            {
              label: "Operations Assignee",
              component: Select,
              apiField: "operations_assignee",
              componentProps: {
                options: operationUsers,
              },
            },
          ],
        },
        {
          cardNumber: 3,
          cardName: "Authorized Representative",
          fields: [
            {
              label: "Name",
              component: TextInput,
              apiField: "contact_name",
              componentProps: {
                placeholder: "Contact Name",
              },
            },
            {
              label: "Title",
              component: TextInput,
              apiField: "contact_title",
              componentProps: {
                placeholder: "Contact Title",
              },
            },
            {
              label: "Email",
              component: TextInput,
              apiField: "contact_email",
              componentProps: {
                placeholder: "Contact Email",
              },
            },
            {
              label: "Phone",
              component: MaskedInput,
              apiField: "contact_phone",
              componentProps: {
                mask: "phone",
              },
            },
            {
              label: "Address",
              component: TextInput,
              apiField: "contact_street",
              componentProps: {
                placeholder: "Contact Address",
              },
            },
            {
              label: "City",
              component: TextInput,
              apiField: "contact_city",
              componentProps: {
                placeholder: "Contact City",
              },
            },
            {
              label: "State",
              component: Select,
              apiField: "contact_state",
              componentProps: {
                options: [...states],
              },
            },
            {
              label: "Zip",
              component: MaskedInput,
              apiField: "contact_zipcode",
              componentProps: {
                type: "text",
                mask: "zipcode",
              },
            },
          ],
        },
        {
          cardNumber: 4,
          cardName: "Account Manager",
          fields: [
            {
              label: "Name",
              component: TextInput,
              apiField: "account_mgr_name",
              componentProps: {
                placeholder: "Account Manager Name",
              },
            },
            {
              label: "Title",
              component: TextInput,
              apiField: "account_mgr_title",
              componentProps: {
                placeholder: "Account Manager Title",
              },
            },
            {
              label: "Email",
              component: TextInput,
              apiField: "account_mgr_email",
              componentProps: {
                placeholder: "Account Manager Email",
              },
            },
            {
              label: "Phone",
              component: MaskedInput,
              apiField: "account_mgr_phone",
              componentProps: {
                mask: "phone",
              },
            },
            {
              label: "Address",
              component: TextInput,
              apiField: "account_mgr_street",
              componentProps: {
                placeholder: "Account Manager Street",
              },
            },
            {
              label: "City",
              component: TextInput,
              apiField: "account_mgr_city",
              componentProps: {
                placeholder: "Account Manager City",
              },
            },
            {
              label: "State",
              component: Select,
              apiField: "account_mgr_state",
              componentProps: {
                options: [...states],
              },
            },
            {
              label: "Zip",
              component: MaskedInput,
              apiField: "account_mgr_zipcode",
              componentProps: {
                type: "text",
                mask: "zipcode",
              },
            },
          ],
        },
        {
          cardNumber: 5,
          cardName: "Submission Agent",
          fields: [
            {
              label: "Name",
              component: TextInput,
              apiField: "submission_agent_name",
              componentProps: {
                placeholder: "Submission Agent Name",
              },
            },
            {
              label: "Title",
              component: TextInput,
              apiField: "submission_agent_title",
              componentProps: {
                placeholder: "Submission Agent Title",
              },
            },
            {
              label: "Email",
              component: TextInput,
              apiField: "submission_agent_email",
              componentProps: {
                placeholder: "Submission Agent Email",
              },
            },
            {
              label: "Phone",
              component: MaskedInput,
              apiField: "submission_agent_phone",
              componentProps: {
                mask: "phone",
              },
            },
            {
              label: "Address",
              component: TextInput,
              apiField: "submission_agent_street",
              componentProps: {
                placeholder: "Submission Agent Address",
              },
            },
            {
              label: "City",
              component: TextInput,
              apiField: "submission_agent_city",
              componentProps: {
                placeholder: "Submission Agent City",
              },
            },
            {
              label: "State",
              component: Select,
              apiField: "submission_agent_state",
              componentProps: {
                options: [...states],
              },
            },
            {
              label: "Zip",
              component: MaskedInput,
              apiField: "submission_agent_zipcode",
              componentProps: {
                type: "text",
                mask: "zipcode",
              },
            },
          ],
        },
      ],
    },
  ];
};

export const getCmsCommunicationForms = (
  constants: ConstantTypes,
  showQueryTimeframe: boolean,
  showClaimTimeframe: boolean,
  isApprovable: boolean,
): SubPageType[] => {
  const cmsCommunicationModeOptions =
    constants?.["CMS_COMMUNICATION_MODE"] || [];

  return [
    {
      name: "CMS Transmission Settings",
      stepNumber: 1,
      path: "cms-communication",
      cards: [
        {
          cardNumber: 1,
          cardName: "CMS Transmission Settings",
          fields: [
            {
              label: "Query File Communication Mode",
              component: Select,
              apiField: "query_file_communication_mode",
              componentProps: {
                options: cmsCommunicationModeOptions,
              },
            },
            {
              label: "Claim File Communication Mode",
              component: Select,
              apiField: "claim_file_communication_mode",
              componentProps: {
                options: cmsCommunicationModeOptions,
              },
            },
            showQueryTimeframe && {
              label: "Query Input Day of Month",
              component: Select,
              apiField: "query_input_send_day",
              componentProps: {
                options: constants?.["QUERY_INPUT_SEND_DAY"] || [],
              },
            },
            showClaimTimeframe && {
              label: "Submission Timeframe Group",
              component: Select,
              apiField: "submission_timeframe_group",
              componentProps: {
                options: constants?.["SUBMISSION_TIME_FRAMES"] || [],
              },
            },
            {
              label: "Approve Query File",
              component: Checkbox,
              apiField: "rre_profile.is_approved_for_query_submission",
              componentProps: {
                readOnly: !isApprovable,
                disabledReason: !isApprovable
                  ? "Please resolve RRE or RRETIN validation errors"
                  : "",
              },
            },
            {
              label: "Approve Claim File",
              component: Checkbox,
              apiField: "rre_profile.is_approved_for_claim_submission",
              componentProps: {
                readOnly: !isApprovable,
                disabledReason: !isApprovable
                  ? "Please resolve RRE or RRETIN validation errors"
                  : "",
              },
            },
            {
              label: "Send test files",
              component: Checkbox,
              apiField: "is_cms_testing_active",
              componentProps: {},
            },
          ].filter(Boolean),
        },
      ],
    },
  ];
};

export const getPolicyForms = (constants: ConstantTypes): SubPageType[] => {
  return [
    {
      name: "Policy Information",
      stepNumber: 1,
      path: "policy-info",
      cards: [
        {
          cardNumber: 1,
          cardName: "Basic Policy Information",
          fields: [
            {
              label: "Policy Name",
              component: TextInput,
              apiField: "name",
              componentProps: {
                placeholder: "Policy Name",
              },
            },
            {
              label: "Policy Number",
              component: TextInput,
              apiField: "policy_number",
              componentProps: {
                placeholder: "Policy Number",
              },
            },
            {
              label: "Plan Insurance Type",
              component: Select,
              apiField: "plan_insurance_type",
              componentProps: {
                options: [...(constants?.["PLAN_INSURANCE_TYPE"] || [])],
              },
            },
            {
              label: "Is Self Insured",
              component: Select,
              apiField: "is_self_insured",
              componentProps: {
                options: constants ? constants["TRUE_FALSE_CHOICES"] : null,
              },
            },
            {
              label: "Self Insured Type",
              component: Select,
              apiField: "self_insured_type",
              componentProps: {
                options: [...(constants?.["SELF_INSURED_TYPE"] || [])],
              },
            },
            {
              label: "Policyholder First Name",
              component: TextInput,
              apiField: "policyholder_first_name",
              componentProps: {
                placeholder: "Policyholder First Name",
              },
            },
            {
              label: "Policyholder Last Name",
              component: TextInput,
              apiField: "policyholder_last_name",
              componentProps: {
                placeholder: "Policyholder Last Name",
              },
            },

            {
              label: "DBA Name",
              component: TextInput,
              apiField: "dba_name",
              componentProps: {
                placeholder: "DBA Name",
              },
            },
            {
              label: "Legal Name",
              component: TextInput,
              apiField: "legal_name",
              componentProps: {
                placeholder: "Legal Name",
              },
            },
          ],
        },
        {
          cardNumber: 2,
          cardName: "Plan Contact Information",
          fields: [
            {
              label: "Plan Contact Department Name",
              component: TextInput,
              apiField: "plan_contact_department_name",
              componentProps: {
                placeholder: "Plan Contact Department Name",
              },
            },
            {
              label: "Plan Contact First Name",
              component: TextInput,
              apiField: "plan_contact_first_name",
              componentProps: {
                placeholder: "Plan Contact First Name",
              },
            },
            {
              label: "Plan Contact Last Name",
              component: TextInput,
              apiField: "plan_contact_last_name",
              componentProps: {
                placeholder: "Plan Contact Last Name",
              },
            },

            {
              label: "Plan Contact Phone",
              component: MaskedInput,
              apiField: "plan_contact_phone",
              componentProps: {
                mask: "phone",
              },
            },
            {
              label: "Plan Contact Phone Extension",
              component: TextInput,
              apiField: "plan_contact_phone_extension",
              componentProps: {
                placeholder: "Phone Extension",
              },
            },
          ],
        },
        {
          cardNumber: 3,
          cardName: "Insurance Limit Information",
          fields: [
            {
              label: "No Fault Insurance Limit",
              component: MaskedInput,
              apiField: "no_fault_insurance_limit",
              componentProps: {
                mask: "number",
              },
            },
          ],
        },
      ],
    },
  ];
};
